import { Device, Document, SkillMetadata, TaskState } from "@models";
import {
  ExtensionOutlined,
  ImageOutlined,
  InsertDriveFileOutlined,
} from "@mui/icons-material";
import { cn } from "@utils";
import { AtSign, ClipboardList } from "lucide-react";
import React, { useState } from "react";
import { MenuContext } from "src/components/Chat/MenuContext";
import { TooltipIconButton } from "src/components/Chat/TooltipIconButton";
import { ChatSkillContext, SelectableDataKey } from "src/components/Chat/types";
import {
  AttachmentList,
  AttachmentListItem,
} from "src/components/ui/AttachmentList";
import { TemplateConfig } from "src/types";
import {
  docsFilteredBySearch,
  shouldKeepDocumentDistinctInSearchResults,
} from "src/utils";
import { isSystemDataKey } from "src/utils/system-data-keys";
import { FileAttachmentPreview } from "./attachments";

// Custom content block type for documents
export interface DocumentContentBlock {
  type: "document";
  documentId: string;
  documentTitle: string;
  deviceId?: string;
  templateKey: string | null;
  templateTitle: string;
  dataKeyId?: string; // Optional data key ID
  metadata: {
    documentId: string;
    documentTitle: string;
    deviceId?: string;
    templateKey: string | null;
    templateTitle: string;
    dataKeyId?: string; // Optional data key ID
  };
}

export interface GenericContentBlock {
  type: "generic";
  title: string;
  removable: boolean;
}

export interface TaskContentBlock {
  type: "task";
  taskStateId: string;
  taskTitle: string;
  notesDocumentId?: string;
  fileDocumentIds: string[];
  metadata: {
    taskStateId: string;
    taskTitle: string;
    notesDocumentId?: string;
    fileDocumentIds: string[];
  };
}

// Union type for all content blocks
export type ContentBlock =
  | DocumentContentBlock
  | GenericContentBlock
  | TaskContentBlock;

type AvailableDataKey = {
  key: string;
  label?: string;
};

interface ContentBlocksPreviewProps {
  previewMode?: boolean;
  disabled?: boolean;
  attachments?: FileAttachmentPreview[];
  onRemoveAttachment?: (id: string) => void;
  onRetryAttachment?: (id: string) => void;
  onSaveAttachmentChange?: (id: string, saveToEqms: boolean) => void;
  blocks: ContentBlock[];
  onRemove: (idx: number) => void;
  onAddDocument?: (document: DocumentContentBlock) => void;
  onAddTask?: (task: TaskContentBlock) => void;
  extraActions?: React.ReactNode;
  className?: string;
  availableDataKeys?: AvailableDataKey[];
  templatesConfig: TemplateConfig;
  documents: Document[];
  devices?: Pick<Device, "id" | "name">[];
  tasks?: TaskState[];
  attachedSkills?: SkillMetadata[];
  skillContexts?: ChatSkillContext[];
  onAttachedSkillsChange?: (skills: SkillMetadata[]) => void;
}

/**
 * Renders a preview of content blocks with optional remove functionality.
 * Uses cn utility for robust class merging.
 */
export const ContentBlocksPreview: React.FC<ContentBlocksPreviewProps> = ({
  previewMode = false,
  disabled = false,
  attachments = [],
  onRemoveAttachment,
  onRetryAttachment,
  onSaveAttachmentChange,
  blocks,
  onRemove,
  onAddDocument,
  onAddTask,
  extraActions,
  className,
  availableDataKeys = [],
  templatesConfig,
  documents,
  devices,
  tasks = [],
  attachedSkills = [],
  skillContexts = [],
  onAttachedSkillsChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setIsMenuOpen(true);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setIsMenuOpen(false);
    setSearchQuery("");
  };

  const handleDocumentSelect = (doc: Document) => {
    const template =
      templatesConfig?.[doc.templateKey as keyof typeof templatesConfig];
    const title = doc.title || template?.defaultTitle || "Untitled Document";

    const documentBlock: DocumentContentBlock = {
      type: "document",
      documentId: doc.id,
      documentTitle: title,
      ...(doc.deviceId && { deviceId: doc.deviceId }),
      templateKey: doc.templateKey,
      templateTitle: template?.defaultTitle || "",
      metadata: {
        documentId: doc.id,
        documentTitle: title,
        ...(doc.deviceId && { deviceId: doc.deviceId }),
        templateKey: doc.templateKey,
        templateTitle: template?.defaultTitle || "",
      },
    };

    onAddDocument?.(documentBlock);
    handleMenuClose();
  };

  const handleDataKeySelect = (item: SelectableDataKey) => {
    const doc = item.document;
    const template = doc
      ? templatesConfig?.[doc.templateKey as keyof typeof templatesConfig]
      : null;
    const title = doc
      ? doc.title || template?.defaultTitle || "Untitled Document"
      : item.dataKey;
    const documentId = doc ? doc.id : `data-key-${item.dataKey}`;

    const documentBlock: DocumentContentBlock = {
      type: "document",
      documentId,
      documentTitle: title,
      ...(doc?.deviceId && { deviceId: doc.deviceId }),
      templateKey: doc?.templateKey || "data-key",
      templateTitle: template?.defaultTitle || "Data Key",
      dataKeyId: item.dataKey,
      metadata: {
        documentId,
        documentTitle: title,
        ...(doc?.deviceId && { deviceId: doc.deviceId }),
        templateKey: doc?.templateKey || "data-key",
        templateTitle: template?.defaultTitle || "Data Key",
        dataKeyId: item.dataKey,
      },
    };

    onAddDocument?.(documentBlock);
    handleMenuClose();
  };

  const handleTaskSelect = (task: TaskState) => {
    const taskTitle = task.notesDocument?.title || task.taskKey;
    const notesDocumentId = task.notesDocumentId || undefined;
    const fileDocumentIds = (task.fileDocuments || []).map((doc) => doc.id);

    const taskBlock: TaskContentBlock = {
      type: "task",
      taskStateId: task.id,
      taskTitle,
      notesDocumentId,
      fileDocumentIds,
      metadata: {
        taskStateId: task.id,
        taskTitle,
        notesDocumentId,
        fileDocumentIds,
      },
    };

    onAddTask?.(taskBlock);
    handleMenuClose();
  };

  const filteredDocuments = docsFilteredBySearch(documents, searchQuery, {
    templatesConfig,
  });

  // Filter out already selected documents
  const selectedDocumentIds = blocks
    .filter((block): block is DocumentContentBlock => block.type === "document")
    .map((block) => block.documentId);

  const availableDocuments = filteredDocuments.filter(
    (doc) => !selectedDocumentIds.includes(doc.id)
  );

  // Collapse configured generated docs by template key. Keep documents with
  // unknown templates distinct, including documents from other devices.
  const uniqueTemplateDocuments = availableDocuments.reduce((acc, doc) => {
    const template =
      templatesConfig?.[doc.templateKey as keyof typeof templatesConfig];
    const shouldKeepDistinct =
      !template || shouldKeepDocumentDistinctInSearchResults(doc, template);

    if (shouldKeepDistinct) {
      acc.push(doc);
    } else {
      // Keep one configured template per device, not per organization.
      const existingDoc = acc.find(
        (existing) =>
          existing.templateKey === doc.templateKey &&
          existing.deviceId === doc.deviceId
      );
      if (!existingDoc) {
        acc.push(doc);
      }
    }

    return acc;
  }, [] as any[]);

  const selectedDataKeysSet = new Set(
    blocks
      .filter(
        (block): block is DocumentContentBlock =>
          block.type === "document" && !!block.dataKeyId
      )
      .map((block) => `${block.dataKeyId}-${block.documentId}`)
  );

  const selectedDataKeyIds = new Set(
    blocks
      .filter(
        (block): block is DocumentContentBlock =>
          block.type === "document" && !!block.dataKeyId
      )
      .map((block) => block.dataKeyId)
  );

  const selectedSyntheticDataKeyIds = new Set(
    blocks
      .filter(
        (block): block is DocumentContentBlock =>
          block.type === "document" &&
          !!block.dataKeyId &&
          block.documentId === `data-key-${block.dataKeyId}`
      )
      .map((block) => block.dataKeyId)
  );

  const documentDataKeys: SelectableDataKey[] = uniqueTemplateDocuments.flatMap(
    (doc) => {
      const latestVersion = doc.versions?.[0];
      if (!latestVersion?.answers) return [];

      const template =
        templatesConfig?.[doc.templateKey as keyof typeof templatesConfig];
      const documentTitle =
        doc.title || template?.defaultTitle || "Untitled Document";
      const searchLower = searchQuery.toLowerCase();

      return latestVersion.answers
        .map((answer: any) => ({
          dataKey: answer.element,
          document: doc,
          documentTitle,
          source: "document" as const,
        }))
        .filter((item: SelectableDataKey) => {
          if (!item.document) return false;

          const key = `${item.dataKey}-${item.document.id}`;
          if (selectedDataKeysSet.has(key)) return false;
          if (selectedSyntheticDataKeyIds.has(item.dataKey)) return false;

          return (
            item.dataKey.toLowerCase().includes(searchLower) ||
            documentTitle.toLowerCase().includes(searchLower)
          );
        });
    }
  );

  const documentBackedDataKeyIds = new Set(
    documentDataKeys.map((item) => item.dataKey)
  );

  const systemDataKeys: SelectableDataKey[] = availableDataKeys
    .filter((dataKey) => isSystemDataKey(dataKey.key))
    .map((dataKey) => ({
      dataKey: dataKey.key,
      label: dataKey.label,
      source: "system" as const,
    }))
    .filter((item) => {
      const searchLower = searchQuery.toLowerCase();
      if (selectedDataKeyIds.has(item.dataKey)) return false;
      if (documentBackedDataKeyIds.has(item.dataKey)) return false;

      return (
        item.dataKey.toLowerCase().includes(searchLower) ||
        item.label?.toLowerCase().includes(searchLower)
      );
    });

  const filteredDataKeys = [...documentDataKeys, ...systemDataKeys];

  const selectedTaskStateIds = new Set(
    blocks
      .filter((block): block is TaskContentBlock => block.type === "task")
      .map((block) => block.taskStateId)
  );

  const availableTasks = tasks.filter(
    (task) => !selectedTaskStateIds.has(task.id)
  );

  const filteredTasks = availableTasks.filter((task) => {
    if (!searchQuery.trim()) {
      return true;
    }
    const searchLower = searchQuery.toLowerCase();
    const taskTitle = task.notesDocument?.title || task.taskKey;
    return (
      taskTitle.toLowerCase().includes(searchLower) ||
      task.taskKey.toLowerCase().includes(searchLower)
    );
  });

  const getBlockTitle = (block: ContentBlock) =>
    block.type === "task"
      ? block.taskTitle
      : block.type === "document" && block.dataKeyId
        ? block.dataKeyId
        : block.type === "document" && block.documentTitle
          ? block.documentTitle
          : block.type === "generic" && block.title
            ? block.title
            : "";

  const renderStatusIcon = (block: ContentBlock) =>
    block.type === "task" ? (
      <ClipboardList className="size-[14px] shrink-0 text-gray-500" />
    ) : (
      <InsertDriveFileOutlined
        className={cn(
          "size-[14px] shrink-0",
          block.type === "document" ? "text-blue-500" : "text-gray-500"
        )}
      />
    );

  const items: AttachmentListItem[] = [
    ...blocks.map((block, idx) => ({
      id: `context-${idx}`,
      title: getBlockTitle(block),
      icon: renderStatusIcon(block),
      storage:
        block.type === "document" && !block.dataKeyId
          ? { destination: "eqms" as const, saved: true }
          : undefined,
      onRemove:
        !previewMode && !(block.type === "generic" && !block.removable)
          ? () => onRemove(idx)
          : undefined,
    })),
    ...attachments.map((attachment) => ({
      id: `upload-${attachment.id}`,
      title: attachment.filename,
      icon: attachment.imageData ? (
        <img
          alt={attachment.filename}
          src={`data:${attachment.mimeType};base64,${attachment.imageData}`}
          className="size-[14px] shrink-0 rounded-sm object-contain"
        />
      ) : attachment.mimeType.startsWith("image/") ? (
        <ImageOutlined className="size-[14px] shrink-0 text-blue-500" />
      ) : (
        <InsertDriveFileOutlined className="size-[14px] shrink-0 text-blue-500" />
      ),
      storage: {
        destination:
          attachment.documentId || attachment.saveToEqms
            ? ("eqms" as const)
            : ("chat" as const),
        saved: !!attachment.documentId,
        saveStarted: attachment.saveStarted,
        onChange:
          !previewMode && onSaveAttachmentChange
            ? (save: boolean) => onSaveAttachmentChange(attachment.id, save)
            : undefined,
      },
      status: attachment.status,
      error: attachment.error,
      onRemove:
        !previewMode && onRemoveAttachment
          ? () => onRemoveAttachment(attachment.id)
          : undefined,
      onRetry:
        !previewMode && attachment.status === "failed" && onRetryAttachment
          ? () => onRetryAttachment(attachment.id)
          : undefined,
    })),
    ...skillContexts.map((skill) => ({
      id: `skill-context-${skill.id}`,
      title: skill.name,
      icon: (
        <ExtensionOutlined className="size-[14px] shrink-0 text-gray-500" />
      ),
    })),
    ...(!previewMode
      ? attachedSkills.map((skill) => ({
          id: `skill-${skill.id}`,
          title: skill.name,
          icon: (
            <ExtensionOutlined className="size-[14px] shrink-0 text-gray-500" />
          ),
          onRemove: onAttachedSkillsChange
            ? () =>
                onAttachedSkillsChange(
                  attachedSkills.filter((attached) => attached.id !== skill.id)
                )
            : undefined,
        }))
      : []),
  ];

  return (
    <>
      <AttachmentList
        items={items}
        disabled={disabled}
        className={className}
        actions={
          !previewMode && (
            <>
              <TooltipIconButton
                className="border-solid border-gray-300 border rounded-md"
                tooltip="Add context"
                onClick={handleMenuClick}
                disabled={disabled}
                data-documents-button
                size="small"
                variant="outlined"
              >
                <AtSign className="size-[16px] text-gray-500" />
              </TooltipIconButton>
              {extraActions}
            </>
          )
        }
      />
      {!previewMode && (
        <MenuContext
          anchorEl={anchorEl}
          isMenuOpen={isMenuOpen}
          handleMenuClose={handleMenuClose}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          uniqueTemplateDocuments={uniqueTemplateDocuments}
          devices={devices}
          filteredDataKeys={filteredDataKeys}
          filteredTasks={filteredTasks}
          handleDocumentSelect={handleDocumentSelect}
          handleDataKeySelect={handleDataKeySelect}
          handleTaskSelect={handleTaskSelect}
          templatesConfig={templatesConfig}
        />
      )}
    </>
  );
};
